import type { RoomParticipant } from '../useRoomControl';

interface Props {
  participants: RoomParticipant[];
  selfId: string;
  latencyMs: number | null;
  isHost: boolean;
  /** Nom de la personne référencée comme présentatrice. */
  presenterName?: string | null;
  /** L'hôte désigne un participant comme présentateur (partage d'écran). */
  onDesignate?: (participant: RoomParticipant) => void;
}

/** Qualité réseau estimée à partir de la latence aller-retour (ms). */
function networkQuality(latency: number | null): { label: string; icon: string; className: string } {
  if (latency == null) return { label: 'Mesure…', icon: 'ri-signal-tower-line', className: 'text-white/50' };
  if (latency < 120) return { label: 'Excellente', icon: 'ri-signal-wifi-line', className: 'text-emerald-400' };
  if (latency < 250) return { label: 'Bonne', icon: 'ri-signal-wifi-2-line', className: 'text-emerald-300' };
  if (latency < 450) return { label: 'Moyenne', icon: 'ri-signal-wifi-3-line', className: 'text-amber-400' };
  return { label: 'Faible', icon: 'ri-signal-wifi-error-line', className: 'text-red-400' };
}

/** Liste des participants en direct + qualité réseau de la salle. */
export default function PeoplePanel({ participants, selfId, latencyMs, isHost, presenterName, onDesignate }: Props) {
  const quality = networkQuality(latencyMs);
  const count = participants.length;
  const activePresenter = participants.find((p) => p.presenting);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <i className="ri-group-line text-accent-400"></i>
          <span className="text-sm font-semibold text-white">Participants</span>
        </div>
        <span className="text-[11px] text-white/60">{count}</span>
      </div>

      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
        <span className="text-[11px] text-white/50 flex items-center gap-1.5">
          <i className="ri-pulse-line"></i>Qualité réseau
        </span>
        <span className={`text-[11px] font-medium flex items-center gap-1.5 ${quality.className}`}>
          <i className={quality.icon}></i>
          {quality.label}{latencyMs != null ? ` · ${latencyMs} ms` : ''}
        </span>
      </div>

      {(activePresenter || presenterName) && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 bg-accent-500/10">
          <i className="ri-presentation-line text-accent-300"></i>
          <span className="text-[11px] text-accent-200 truncate">
            {activePresenter
              ? `${activePresenter.name} présente son écran`
              : `${presenterName} est désigné présentateur`}
          </span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5">
        {participants.length === 0 ? (
          <div className="text-center py-10 px-4">
            <i className="ri-user-voice-line text-3xl text-white/20"></i>
            <p className="text-xs text-white/40 mt-2">En attente des participants…</p>
          </div>
        ) : (
          participants.map((p) => (
            <div key={p.id} className={`flex items-center gap-2.5 p-2.5 rounded-lg ${p.presenting ? 'bg-accent-500/15 ring-1 ring-accent-500/30' : 'bg-white/5'}`}>
              <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <i className={`${p.presenting ? 'ri-presentation-line' : p.publishing ? 'ri-user-voice-line' : 'ri-user-line'} text-white/70 text-sm`}></i>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white truncate">
                  {p.name}{p.id === selfId ? ' (vous)' : ''}
                </p>
                <p className="text-[10px] text-white/40 truncate">
                  {p.presenting ? 'Présente son écran' : p.isHost ? 'Hôte' : p.publishing ? 'Participe' : 'Spectateur'}
                </p>
              </div>
              {p.handRaised && (
                <span className="w-6 h-6 rounded-full bg-amber-400/20 flex items-center justify-center" title="Main levée">
                  <i className="ri-arrow-up-circle-line text-amber-400 text-xs"></i>
                </span>
              )}
              {p.presenting && (
                <span className="px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-[9px] font-semibold whitespace-nowrap" title="Présente son écran">
                  Présente
                </span>
              )}
              {p.isHost && <i className="ri-vip-crown-2-line text-accent-400 text-sm" title="Hôte"></i>}
              {isHost && !p.isHost && !p.presenting && onDesignate && (
                <button
                  onClick={() => onDesignate(p)}
                  className="w-7 h-7 rounded-full bg-white/10 hover:bg-accent-500 hover:text-background-50 text-white/70 flex items-center justify-center cursor-pointer transition-colors flex-shrink-0"
                  title={`Désigner ${p.name} comme présentateur`}
                >
                  <i className="ri-presentation-line text-xs"></i>
                </button>
              )}
            </div>
          ))
        )}
      </div>
      {isHost && (
        <div className="px-4 py-2 border-t border-white/10 text-[10px] text-white/40 flex items-center gap-1.5">
          <i className="ri-shield-star-line"></i>
          Vous êtes l&apos;hôte : coupez tout le monde, gérez les prises de parole et désignez un présentateur.
        </div>
      )}
    </div>
  );
}